import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U2000GridStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableHandlers } from "../../../u2000Core/types/U2000TableHandlers";
import { NS, U3129ROLES } from "../../U3129";
import type { U3129Timesheet } from "../../U3129Service";

interface U3129TimesheetListToolBarProps<T extends Record<string, unknown>> {
  gridStateHandlers: U2000GridStateHandlers<T>;
  handlerActions?: U2000TableHandlers<T>;
}

export const U3129TimesheetListToolBar: React.FC<U3129TimesheetListToolBarProps<Record<string, unknown>>> = ({
  gridStateHandlers,
  handlerActions,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const disableUpdateBtn = useMemo(() => {
    const selectedRows = gridStateHandlers.getSelectedRows() as unknown as U3129Timesheet[];
    if (selectedRows.length !== 1) return true;
    return selectedRows[0]?.status !== "Open";
  }, [gridStateHandlers.getSelectedRows()]);

  const disableDeleteBtn = useMemo(() => {
    const selectedRows = gridStateHandlers.getSelectedRows() as unknown as U3129Timesheet[];
    if (selectedRows.length === 0) return true;
    return selectedRows.some((r) => r.status !== "Open");
  }, [gridStateHandlers.getSelectedRows()]);

  const actions = [
    {
      key: "add",
      label: t("btnAdd", { ns: NS }),
      hasRole: hasRole(U3129ROLES.ADD),
      handleClick: handlerActions?.onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: "modify",
      label: t("btnModify", { ns: NS }),
      hasRole: hasRole(U3129ROLES.EDIT),
      handleClick: handlerActions?.onModify,
      disabled: disableUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: "delete",
      label: t("btnDelete", { ns: NS }),
      hasRole: hasRole(U3129ROLES.DELETE),
      handleClick: handlerActions?.onDelete,
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};

export default U3129TimesheetListToolBar;
